/**
 * DomSnapshotView — renders a ``dom`` / ``dom_dump`` artifact (full-page
 * HTML snapshot captured next to a screenshot when its selector matched
 * nothing) inside the shared ``ArtifactLightbox`` body slot.
 *
 * Uses an ``<iframe>`` with ``sandbox="allow-same-origin"`` so the dump can
 * resolve its own Epic stylesheet references but cannot execute scripts,
 * navigate the top frame, or submit forms. SAS-signed blob URLs and
 * ``file://`` dev URLs both work via the iframe ``src`` attribute.
 *
 * A header toolbar preserves the legacy "Open in new tab" + Download
 * escape hatches so power users can still leave the modal if they need
 * to inspect the source.
 */
import { useEffect, useState } from "react";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import DownloadIcon from "@mui/icons-material/Download";
import type { WorkflowArtifact } from "./WorkflowArtifactsSection";
import { formatCapturedAt } from "./WorkflowArtifactsSection";

export function DomSnapshotView({ artifact }: { artifact: WorkflowArtifact }) {
  const [iframeError, setIframeError] = useState(false);

  // Reset error state when navigating to a different DOM dump.
  useEffect(() => {
    setIframeError(false);
  }, [artifact.url]);

  const capturedAt = formatCapturedAt(artifact.captured_at);
  const label = artifact.filename ?? artifact.step_name ?? "DOM snapshot";

  return (
    <div
      className="flex flex-col w-full h-full"
      data-testid="dom-snapshot-view"
    >
      <div className="flex items-center justify-between gap-3 px-3 py-1.5 border-b border-primary-gray-light-200 bg-primary-gray-light-25 flex-shrink-0">
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-primary-gray-light-900 truncate">
            {label}
          </p>
          {capturedAt && (
            <p className="text-2xs text-primary-gray-light-500">{capturedAt}</p>
          )}
        </div>
        <a
          href={artifact.url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-sunrise hover:underline inline-flex items-center gap-1"
          data-testid="dom-snapshot-open-new-tab"
        >
          <OpenInNewIcon fontSize="inherit" />
          Open in new tab
        </a>
        <a
          href={artifact.url}
          download={artifact.filename ?? ""}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-primary-gray-light-700 hover:underline inline-flex items-center gap-1"
          data-testid="dom-snapshot-download"
        >
          <DownloadIcon fontSize="inherit" />
          Download
        </a>
      </div>
      <div className="flex-1 min-h-0 bg-base-white">
        {iframeError ? (
          <div className="flex items-center justify-center h-full p-4 text-center text-sm text-primary-gray-light-700">
            Failed to load DOM snapshot inline. Use the links above to view it
            in a new tab or download the HTML.
          </div>
        ) : (
          <iframe
            src={artifact.url}
            title={label}
            sandbox="allow-same-origin"
            className="w-full h-full border-0"
            onError={() => setIframeError(true)}
            data-testid="dom-snapshot-iframe"
          />
        )}
      </div>
    </div>
  );
}

export default DomSnapshotView;
